import { type ReactNode, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HeaderWallet } from "./HeaderWallet";
import { NetworkBanner } from "./NetworkBanner";
import { NotificationBell } from "./NotificationBell";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { trackPageView } from "../lib/xPixel";
import { trackPageView as trackGa4PageView } from "../lib/ga4";
import { MaintenancePage } from "../pages/MaintenancePage";

// Polls the same cache entry `usePublicConfig` reads, just with a much shorter interval — React
// Query applies the shortest active `refetchInterval` among observers of a shared query key, so
// mounting this here speeds up refetches for every consumer app-wide while AppShell is mounted
// (i.e. always, on the public site). Maintenance mode is an active operator push meant to take
// effect quickly, not something that should wait out `usePublicConfig`'s 5-minute staleTime.
const MAINTENANCE_POLL_MS = 15_000;

function NavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink to={to} end={to === "/"}>
      {children}
    </NavLink>
  );
}

/** Footer link groups. Keeping these as data rather than markup makes it obvious at a glance
 *  which public pages exist — and it's the list that must stay in sync with the sitemap in
 *  `server.mjs` and the crawl rules in `public/robots.txt`. */
const FOOTER_SECTIONS: { heading: string; links: { to: string; label: string }[] }[] = [
  {
    heading: "Marketplace",
    links: [
      { to: "/", label: "Browse listings" },
      { to: "/sell", label: "Start selling" },
      { to: "/orders", label: "My purchases" },
    ],
  },
  {
    heading: "Learn",
    links: [
      { to: "/how-it-works", label: "How it works" },
      { to: "/faq", label: "FAQ" },
      { to: "/about", label: "About" },
      { to: "/blog", label: "Blog" },
      { to: "/contact", label: "Contact us" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { to: "/terms", label: "Terms of Service" },
      { to: "/privacy", label: "Privacy Policy" },
    ],
  },
  {
    heading: "Developers",
    links: [{ to: "/api-docs", label: "API documentation" }],
  },
];

export function AppShell() {
  const { isSignedIn, principal } = useAuthPrincipal();
  const isStaff = principal?.role === "MODERATOR" || principal?.role === "ADMIN";
  const { data: config } = useQuery({
    queryKey: queryKeys.publicConfig,
    queryFn: ({ signal }) => apiClient.getPublicConfig(signal),
    refetchInterval: MAINTENANCE_POLL_MS,
  });
  const location = useLocation();

  // Fires on every route the public site actually shows, including the first — the base pixel's
  // own automatic pageview-on-load is a separate, more basic signal that doesn't apply this
  // named event's configured attribution windows and never fires again on a client-side route
  // change. AppShell only wraps the public marketing site (not /admin/*), which is the right
  // scope for ad-campaign traffic measurement and organic-search analytics alike.
  useEffect(() => {
    trackPageView();
    trackGa4PageView(location.pathname);
  }, [location.pathname]);

  if (config?.maintenanceMode) {
    return <MaintenancePage message={config.maintenanceMessage} />;
  }

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header className="app-header">
        <div className="container app-header__bar">
          {/* Link, not a bare anchor: still renders a crawlable <a href="/"> but navigates
              client-side instead of forcing a full document reload. */}
          <Link to="/" className="brand">
            MarketOn<span>Base</span>
          </Link>
          <nav className="main-nav" aria-label="Primary">
            <NavItem to="/">Browse</NavItem>
            <NavItem to="/how-it-works">How it works</NavItem>
            {isSignedIn && <NavItem to="/orders">My purchases</NavItem>}
            {isSignedIn && <NavItem to="/messages">Messages</NavItem>}
            {isSignedIn && <NavItem to="/watchlist">Watchlist</NavItem>}
            {isSignedIn && <NavItem to="/tickets">Support</NavItem>}
            {isSignedIn && <NavItem to="/sell">Sell</NavItem>}
            {isSignedIn && <NavItem to="/profile">Profile</NavItem>}
            {isStaff && <NavItem to="/moderation">Moderation</NavItem>}
          </nav>
          <div className="row" style={{ gap: "var(--space-2)" }}>
            {isSignedIn && <NotificationBell />}
            <HeaderWallet />
          </div>
        </div>
      </header>
      <main id="main-content" className="app-main">
        <div className="container">
          <NetworkBanner />
          <Outlet />
        </div>
      </main>
      <footer className="app-footer">
        <div className="container stack">
          <nav className="footer-nav" aria-label="Footer">
            {FOOTER_SECTIONS.map((section) => (
              <div key={section.heading}>
                <h2>{section.heading}</h2>
                <ul>
                  {section.links.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <p>
            MarketOnBase uses a secure smart contract on Base. Cryptocurrency transactions
            are irreversible. Disputes must be opened before the order&apos;s dispute deadline — see
            each order for its exact window.
          </p>
          <p>
            Nothing on MarketOnBase constitutes legal, financial, or regulatory advice. See our{" "}
            <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>{" "}
            for details.
          </p>
        </div>
      </footer>
    </>
  );
}
